import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ResizeObserver } from '@juggle/resize-observer';
import '../../../../utils/index.js';
import MenuSearchBox from '../MenuSearchBox.js';
import '../../../../hooks/index.js';
import { VIRTUOSO_PROPS } from '../../../../next/Menu/constants.js';
import { useIsFocusVisible, conditionalA11yHelpersWrapper } from '../../../UIProvider/A11yHelpersContext.js';
import useId from '../../../../hooks/useId/useId.js';
import wrapEvent from '../../../../utils/wrapEvent/wrapEvent.js';
import mergeRefs from '../../../../utils/mergeRefs/mergeRefs.js';
import { SUBMENU_POPPER_OFFSET, FLYOUT_POPPER_OFFSET } from '../../../../utils/constants.js';

const ArrowNav = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  var _process$env, _childRefs$current$le, _containerRef$current3;
  let {
    role,
    tabIndex = -1,
    highlightedIndex,
    onHighlightedIndexChange,
    children,
    searchBoxRef,
    anchorRef,
    isSubmenu,
    onKeyDown,
    isNavItem = () => true,
    maxHeight,
    minWidth,
    searchable,
    searchTerm,
    setSearchTerm,
    isVirtualized = false,
    'data-popper-ready': isPopperReady,
    // comes from Popper.js where ArrowNav is cloned
    style,
    'aria-labelledby': ariaLabelledBy,
    ...rest
  } = _ref;
  const containerRef = useRef(null);
  const virtuosoRef = useRef(null);
  const childRefs = useRef({});
  const [virtualRange, setVirtualRange] = useState({
    startIndex: -1,
    endIndex: -1
  });
  const menuId = useId();
  const {
    isFocusVisible
  } = useIsFocusVisible();
  const getAdditionalProps = useCallback((child, index) => ({
    tabIndex: 0,
    onMouseEnter: wrapEvent(child.props.onMouseEnter, () => {
      onHighlightedIndexChange(index);
    })
  }), [onHighlightedIndexChange]);
  const createRefCallback = useCallback(
  // eslint-disable-next-line no-return-assign
  index => node => childRefs.current[index] = node, []);

  // Fixes ResizeObserver error that appears in jest when running tests with a virtualized Menu
  if (typeof process !== 'undefined' && ((_process$env = process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'test' && window) {
    var _window$ResizeObserve;
    window.ResizeObserver = (_window$ResizeObserve = window.ResizeObserver) !== null && _window$ResizeObserve !== void 0 ? _window$ResizeObserve : ResizeObserver;
  }
  const allChildren = useMemo(() => {
    let refIndex = searchable ? 1 : 0;
    const result = React__default.Children.map(children, child => {
      if (isNavItem(child)) {
        const myIndex = refIndex;
        refIndex += 1;
        const additionalProps = getAdditionalProps(child, myIndex);
        return /*#__PURE__*/React__default.cloneElement(child, {
          ref: mergeRefs(createRefCallback(myIndex), child.ref),
          ...additionalProps
        });
      }
      return child;
    });
    childRefs.current.length = refIndex;
    return result;
  }, [children, getAdditionalProps, isNavItem, searchable, createRefCallback]);

  // Update the focused menu item when the highlighted index changes
  useEffect(() => {
    if (isPopperReady) {
      const searchInputHeight = searchable ? 53 : 0;
      const paddingSize = 4;
      if (isFocusVisible && isVirtualized && virtuosoRef.current && (highlightedIndex > virtualRange.endIndex || highlightedIndex < virtualRange.startIndex)) {
        virtuosoRef.current.scrollToIndex({
          index: highlightedIndex,
          offset: -1 * searchInputHeight - paddingSize
        });
      } else if (childRefs.current[highlightedIndex]) {
        // Scroll the item into view when navigating with a keyboard
        const element = childRefs.current[highlightedIndex];
        if (containerRef.current && isFocusVisible) {
          const scrollBottom = containerRef.current.clientHeight + containerRef.current.scrollTop;
          const elementBottom = element.offsetTop + element.offsetHeight;
          if (elementBottom > scrollBottom - searchInputHeight) {
            if (isVirtualized && virtuosoRef.current) {
              virtuosoRef.current.scrollToIndex({
                index: highlightedIndex - (searchable ? 1 : 0),
                align: 'end',
                offset: paddingSize
              });
            } else {
              containerRef.current.scrollTop = elementBottom - containerRef.current.clientHeight;
            }
          } else if (element.offsetTop < containerRef.current.scrollTop) {
            if (isVirtualized && virtuosoRef.current) {
              virtuosoRef.current.scrollToIndex({
                index: highlightedIndex - (searchable ? 1 : 0),
                offset: -1 * searchInputHeight - paddingSize
              });
            } else {
              containerRef.current.scrollTop = element.offsetTop - searchInputHeight;
            }
          }
        }

        /* preventScroll isn't strictly needed.
         * For keyboard users it makes no difference since the item is already in view when .focus() is called.
         * For mouse users, it prevents scrolling from occurring when hovering over a menu item that is partially out of view.
         */
        element.focus({
          preventScroll: true
        });
      } else if (highlightedIndex === 0 && searchable) {
        searchBoxRef === null || searchBoxRef === void 0 || searchBoxRef.current.focus();
      } else {
        var _anchorRef$current;
        anchorRef === null || anchorRef === void 0 || (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 || _anchorRef$current.focus();
      }
    }
  }, [highlightedIndex, childRefs, virtualRange, searchable, allChildren === null || allChildren === void 0 ? void 0 : allChildren.length,
  // Needed to prevent QuickSearch from losing focus in some cases when items are loaded asynchronously while the user is navigating
  isPopperReady, searchBoxRef, anchorRef, isVirtualized, isFocusVisible]);
  const numChildren = (_childRefs$current$le = childRefs.current.length) !== null && _childRefs$current$le !== void 0 ? _childRefs$current$le : 1;
  const handleKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); // prevent scroll
      if (highlightedIndex !== -1) {
        onHighlightedIndexChange((highlightedIndex + 1) % numChildren);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); // prevent scroll
      if (highlightedIndex !== -1) {
        onHighlightedIndexChange((highlightedIndex - 1 + numChildren) % numChildren);
      }
    } else if (e.key === 'PageUp' || e.key === 'Home') {
      var _containerRef$current;
      // Only call the highlighted index change handler for the current nav list (i.e. if the event bubbles up to a parent nav list, don't call the highlighted index change handler on the parent nav list)
      if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(e.target)) {
        e.preventDefault();
        onHighlightedIndexChange(0);
      }
    } else if (e.key === 'PageDown' || e.key === 'End') {
      var _containerRef$current2;
      // Only call the highlighted index change handler for the current nav list (i.e. if the event bubbles up to a parent nav list, don't call the highlighted index change handler on the parent nav list)
      if ((_containerRef$current2 = containerRef.current) !== null && _containerRef$current2 !== void 0 && _containerRef$current2.contains(e.target)) {
        e.preventDefault();
        const lastIndex = numChildren - 1;
        onHighlightedIndexChange(lastIndex);
      }
    }
  };
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React__default.createElement("div", _extends({
      ref: mergeRefs(containerRef, ref),
      tabIndex: tabIndex,
      onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
      "data-popper-ready": isPopperReady,
      style: {
        ...style,
        minWidth,
        maxHeight: maxHeight !== null && maxHeight !== void 0 ? maxHeight :
        // This logic is located here instead of BaseMenu.js so the maxHeight will update with screen resizing (Popper has a resize listener that causes this to rerender)
        Math.min(350, (isSubmenu ?
        // Submenus: window height - submenu popper offset - 8px of container padding - half of anchor height (fallback to default 18px))
        document.documentElement.clientHeight - SUBMENU_POPPER_OFFSET[0] :
        // Menus: half of window height - menu popper offset - 8px of container padding - half of anchor height (fallback to default 18px)
        document.documentElement.clientHeight / 2 - FLYOUT_POPPER_OFFSET[1]) - 8 - (anchorRef !== null && anchorRef !== void 0 && anchorRef.current ? anchorRef.current.offsetHeight / 2 : 18))
      }
    }, rest), searchable && /*#__PURE__*/React__default.createElement(MenuSearchBox, {
      "aria-controls": menuId,
      value: searchTerm,
      ref: searchBoxRef,
      onChange: e => {
        setSearchTerm === null || setSearchTerm === void 0 || setSearchTerm(e.target.value);
      },
      onFocus: () => onHighlightedIndexChange(0)
    }), /*#__PURE__*/React__default.createElement("div", {
      id: menuId,
      role: role,
      "aria-labelledby": ariaLabelledBy,
      style: {
        padding: '4px 0'
      }
    }, isVirtualized ? /*#__PURE__*/React__default.createElement(Virtuoso, _extends({
      customScrollParent: (_containerRef$current3 = containerRef.current) !== null && _containerRef$current3 !== void 0 ? _containerRef$current3 : undefined,
      data: React__default.Children.toArray(allChildren),
      itemContent: (_, child) => ! /*#__PURE__*/React__default.isValidElement(child) ? null : child,
      rangeChanged: setVirtualRange,
      ref: virtuosoRef
    }, VIRTUOSO_PROPS)) : allChildren))
  );
});
var ArrowNav$1 = conditionalA11yHelpersWrapper(ArrowNav);

export { ArrowNav$1 as default };
